import { InputInputEventDetail, IonInputCustomEvent } from '@ionic/core';
import {
    IonCard,
    IonIcon,
    IonImg,
    IonItem,
    IonList,
    IonSelectOption,
    useIonActionSheet,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { closeCircleOutline } from 'ionicons/icons';
import { useAtom, useSetAtom } from 'jotai';
import debounce from 'lodash.debounce';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import * as Yup from 'yup';

import LoadableContent from '../../../components/content/LoadableContent';
import DefaultInput from '../../../components/forms/defaultinput/DefaultInput';
import DefaultSelect from '../../../components/forms/defaultselect/DefaultSelect';
import QRCodeScannerInput from '../../../components/forms/qrcodescannerinput/QRCodeScannerInput';
import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import PhotoUpload from '../../../components/photoUpload/PhotoUpload';
import { TransServerMessage } from '../../../components/translations/TransServerMessage';
import { IHeaterModel } from '../../../interfaces/IHeaterModel';
import { IJob } from '../../../interfaces/IJob';
import { IRoomWithDevices } from '../../../interfaces/IOdooRoomsResponse';
import { IInstallationRoomDevicesRouteProps } from '../../../interfaces/IRoute';
import {
    accessTokenAtom,
    deepomaticAtom,
    devicesListLoadableAtom,
    installationWireDevicesLoadableAtom,
    updateDBStateAtom,
} from '../../../store/Atoms';
import {
    createDeviceRequest,
    deleteDeviceRequest,
    getHeaterBrandList,
    getHeaterModelList,
    updateDeviceRequest,
} from '../../../store/Requests';
import {
    ConnectionType,
    ControlType,
    DeepomaticTaskGroups,
    DeepomaticTasksWithOCR,
} from '../../../types/Generic';
import { THomeAssetData } from '../../../types/HomeAssetData';
import { ImageCategories } from '../../../types/ImageInfo';
import { NoAnimationBuilder } from '../../../utils/animations';
import { MAX_IMAGE_NR } from '../../../utils/constants';
import {
    checkAssignDeepomaticTaskgroupIdx,
    getDeepomaticTaskgroup,
} from '../../../utils/deepomatic';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../../utils/hooks/usePhotosUploaded';
import classes from './Devices.module.css';

type DevicesCreateFormValues = {
    macAddress: string;
};

type SetFieldValue = (
    field: string,
    value: any,
    shouldValidate?: boolean
) => void;

type IonInputEvent = IonInputCustomEvent<InputInputEventDetail>;

type IonFocusEvent = IonInputCustomEvent<FocusEvent>;

export type DevicesUpdateFormValues = {
    location: string;
    heaterLocation: string;
    // heaterType: string;
    underWindow: string;
    wallSurface: string;
    holderQRCode: string;
    fallenDboxNanotapeOnWall: string;
    fallenDboxNanotapeType: string;
    fallenDboxNanotapeScrewHoles: string;
    nominalPowerKw: string;
    brand: string;
    model: string;
};

const DevicesCreateSchema = () =>
    Yup.object().shape({
        macAddress: Yup.string()
            .matches(/^[0-9A-Fa-f]+$/, t`Invalid mac address`)
            .min(6, t`Device mac is too short! Min 6 chars`)
            .max(16, t`Device mac is too long! Max 16 chars`)
            .required(t`Device mac is required`),
    });

const DevicesUpdateSchema = (
    holderQRCodeSkipped: boolean = false,
    interventionReasonCodes: { code: string; reason: string }[] = []
) => {
    const fallenDboxReason = interventionReasonCodes
        .map((el) => el.code)
        .includes('RIR_0001');

    return Yup.object().shape({
        brand: Yup.string().required(t`Please insert the heater's brand`),
        model: Yup.string().required(t`Please insert the heater's model`),
        nominalPowerKw: Yup.string().matches(
            /^\d{1,9}\.\d{1,2}$/,
            t`Invalid value. Allowed numerical format: X.YZ`
        ),
        location: Yup.string().required(
            t`Please describe the location of the heater`
        ),
        heaterLocation: Yup.string().required(t`Please select an option`),
        // heaterType: Yup.string().required(t`Please select heater type`),
        underWindow: Yup.string().required(t`Please select an option`),
        wallSurface: Yup.string().required(t`Please select an option`),
        holderQRCode: holderQRCodeSkipped
            ? Yup.string().matches(
                  /^.+\/\d{4}-\d{2}-\d{2}$/,
                  t`Invalid holder QR code`
              )
            : Yup.string()
                  .required(t`Please scan Holder QR Code`)
                  .matches(
                      /^.+\/\d{4}-\d{2}-\d{2}$/,
                      t`Invalid holder QR code`
                  ),
        fallenDboxNanotapeOnWall: fallenDboxReason
            ? Yup.string().required(
                  t`Please indicate if the nanotape is still on the wall.`
              )
            : Yup.string(),
        fallenDboxNanotapeType: fallenDboxReason
            ? Yup.string().required(
                  t`Please indicate if the nanotape is hard or soft.`
              )
            : Yup.string(),
        fallenDboxNanotapeScrewHoles: fallenDboxReason
            ? Yup.string().required(
                  t`Please indicate if the nanotape has screw holes.`
              )
            : Yup.string(),
    });
};

const Devices: React.FC<IInstallationRoomDevicesRouteProps> = ({ match }) => {
    const jobId = +match.params.jobId;
    const roomId = +match.params.roomId;
    const deviceId = match.params.deviceId
        ? +match.params.deviceId
        : undefined;
    const router = useCustomRouter();
    const [presentActionSheet] = useIonActionSheet();
    const photosUploaded = usePhotosUploaded();
    const [isDeleting, setIsDeleting] = useState(false);
    const [presentToast] = useIonToast();
    const [accessToken] = useAtom(accessTokenAtom);
    const [value] = useAtom(installationWireDevicesLoadableAtom);
    const saveDevices = useSetAtom(devicesListLoadableAtom);
    const [deepomatic, setDeepomatic] = useAtom(deepomaticAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [isDeepomaticSlotFreedUp, setIsDeepomaticSlotFreedUp] =
        useState(false);
    const [showHeaterBrandList, setShowHeaterBrandList] = useState(false);
    const [showHeaterModelList, setShowHeaterModelList] = useState(false);
    const [heaterBrands, setHeaterBrands] = useState<string[]>([]);
    const [heaterModels, setHeaterModels] = useState<IHeaterModel[]>([]);
    const formRef = useRef(null);
    const heatersBrandsRequestControllerRef = useRef<AbortController>();
    const heatersModelsRequestControllerRef = useRef<AbortController>();
    const room: IRoomWithDevices | undefined =
        (value.state === 'hasData' &&
            value.data.rooms.find((r) => r.roomId === roomId)) ||
        undefined;
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data.rooms : []),
        [value]
    );
    const jobDetails: IJob | undefined = useMemo(
        () => (value.state === 'hasData' ? value.data.jobDetails : undefined),
        [value]
    );
    const device = useMemo(
        () => room?.devices.find((d) => d.id === deviceId),
        [room, deviceId]
    );
    const deviceHeaterLocationTypes =
        value.state === 'hasData'
            ? value.data.config.selects.deviceHeaterLocationTypes
            : [];
    const deviceUnderWindowTypes =
        value.state === 'hasData'
            ? value.data.config.selects.deviceUnderWindowTypes
            : [];
    const deviceWallSurfaceTypes =
        value.state === 'hasData'
            ? value.data.config.selects.deviceWallSurfaceTypes
            : [];
    const deviceFallenDboxNanotapeOnWallTypes =
        value.state === 'hasData'
            ? value.data.config.selects.deviceFallenDboxNanotapeOnWallTypes
            : [];
    const deviceFallenDboxNanotapeTypeTypes =
        value.state === 'hasData'
            ? value.data.config.selects.deviceFallenDboxNanotapeTypeTypes
            : [];
    const deviceFallenDboxNanotapeScrewHolesTypes =
        value.state === 'hasData'
            ? value.data.config.selects.deviceFallenDboxNanotapeScrewHolesTypes
            : [];
    const updateBrandListOnInput = useCallback(
        async (e: IonInputEvent) => {
            let query = '';
            const target = e.target as HTMLIonInputElement;
            if (target) query = target.value!.toString().toLowerCase();
            if (query.length >= 3) {
                const brands = await getHeaterBrandList({
                    accessToken: accessToken,
                    brand: query,
                    controllerRef: heatersBrandsRequestControllerRef,
                });
                setHeaterBrands(brands);
                setShowHeaterBrandList(true);
            } else {
                setShowHeaterBrandList(false);
            }
        },
        [accessToken]
    );
    const debouncedUpdateBrandList = useMemo(() => {
        return debounce((e: IonInputEvent) => {
            void updateBrandListOnInput(e);
        }, 300);
    }, [updateBrandListOnInput]);
    const handleBrandOnInput = (
        e: IonInputEvent,
        handleChange: (e: IonInputEvent) => void
    ) => {
        handleChange(e);
        debouncedUpdateBrandList(e);
    };
    const updateModelListOnInput = useCallback(
        async (e: IonInputEvent, brand: string) => {
            let query = '';
            const target = e.target as HTMLIonInputElement;
            if (target) query = target.value!.toString().toLowerCase();
            if (query.length >= 3) {
                const models = await getHeaterModelList({
                    accessToken: accessToken,
                    model: query,
                    brand: brand,
                    controllerRef: heatersModelsRequestControllerRef,
                });
                setHeaterModels(models);
                setShowHeaterModelList(true);
            } else {
                setShowHeaterModelList(false);
            }
        },
        [accessToken]
    );
    const debouncedUpdateModelList = useMemo(() => {
        return debounce((e: IonInputEvent, brand: string) => {
            void updateModelListOnInput(e, brand);
        }, 300);
    }, [updateModelListOnInput]);
    const handleModelOnInput = (
        e: IonInputEvent,
        handleChange: (e: IonInputEvent) => void,
        brand: string
    ) => {
        handleChange(e);
        debouncedUpdateModelList(e, brand);
    };
    const handleModelOnBlur = (
        e: IonFocusEvent,
        handleBlur: (e: IonFocusEvent) => void
    ) => {
        setShowHeaterModelList(false);
        handleBlur(e);
    };
    const handleBrandOnBlur = (
        e: IonFocusEvent,
        handleBlur: (e: IonFocusEvent) => void
    ) => {
        setShowHeaterBrandList(false);
        handleBlur(e);
    };
    const handleHeaterModelClick = (
        model: string,
        brand: string,
        setFieldValue: SetFieldValue
    ) => {
        setFieldValue('model', model);
        if (!brand) setFieldValue('brand', brand);
    };
    const handleHeaterBrandClick = (
        brand: string,
        setFieldValue: SetFieldValue
    ) => {
        setFieldValue('brand', brand);
    };
    const handleFieldReset = (
        fieldName: string,
        setFieldValue: SetFieldValue
    ) => {
        const resetValue =
            fieldName === 'brand' ? device?.brand : device?.model;
        setFieldValue(fieldName, resetValue);
    };
    useEffect(() => {
        if (device && !isDeepomaticSlotFreedUp) {
            void checkAssignDeepomaticTaskgroupIdx(
                device.macAddress,
                deepomatic,
                setDeepomatic,
                updateDBState
            );
        }
        return () => {
            debouncedUpdateBrandList.cancel();
            debouncedUpdateModelList.cancel();
        };
    }, [
        jobId,
        roomId,
        deviceId,
        device,
        deepomatic,
        setDeepomatic,
        updateDBState,
        isDeepomaticSlotFreedUp,
        debouncedUpdateBrandList,
        debouncedUpdateModelList,
    ]);
    const updateUrl = useCallback(
        (deviceId: number) => {
            router.push(
                `/${jobId}/installation/wire/rooms/${roomId}/devices/${deviceId}`,
                'none',
                'replace',
                undefined,
                NoAnimationBuilder
            );
        },
        [jobId, roomId, router]
    );

    const showSuccessMessage = useCallback(() => {
        presentToast({
            message: t`Device saved successfully`,
            duration: 5000,
            color: 'success',
            buttons: [
                {
                    text: t`Dismiss`,
                    role: 'cancel',
                },
            ],
        });
    }, [presentToast]);

    const deleteDevice = useCallback(async () => {
        setIsDeleting(true);

        try {
            const devicesInCache = rooms.reduce((acc: THomeAssetData[], r) => {
                return [...acc, ...r.devices.map((d) => d)];
            }, []);
            const devicesToKeep = devicesInCache.filter(
                (d) => d.id !== deviceId
            );

            if (!deviceId) {
                throw new Error('Device ID is missing');
            }
            const deviceToDelete = devicesInCache.filter(
                (d) => d.id === deviceId
            );
            const mac = deviceToDelete[0].macAddress;
            // free slot from deepomatic array, if device present
            const idx = deepomatic.findIndex((e) => e === mac);
            if (idx !== -1) {
                let deepomaticCopy = [...deepomatic];
                deepomaticCopy[idx] = null;
                // the below state prevents the method inside the useEffect
                // from re-assigning a spot to the deleted device
                setIsDeepomaticSlotFreedUp(true);
                setDeepomatic(deepomaticCopy);
            }
            //update db to reflect updated deepomatic array
            await updateDBState();
            await deleteDeviceRequest(accessToken, jobId, deviceId);
            saveDevices(devicesToKeep);

            router.goBack();
        } catch (e: any) {
            const response = e.response;
            const errorMessage = response.rooms
                ? response.rooms.reduce((acc: string, message: string) => {
                      return `${acc}${message}<br/>`;
                  }, '')
                : e.reason;
            presentToast({
                message: errorMessage,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }

        setIsDeleting(false);
    }, [
        rooms,
        deviceId,
        accessToken,
        jobId,
        saveDevices,
        router,
        presentToast,
        deepomatic,
        setDeepomatic,
        updateDBState,
    ]);

    const handleDeleteDevice = useCallback(() => {
        presentActionSheet({
            header: t`Delete Device`,
            subHeader: t`Are you sure you want to delete this device?`,
            buttons: [
                {
                    text: t`Delete`,
                    role: 'destructive',
                    data: {
                        action: 'delete',
                    },
                },
                {
                    text: t`Cancel`,
                    role: 'cancel',
                    data: {
                        action: 'cancel',
                    },
                },
            ],
            onWillDismiss: ({ detail }) => {
                if (detail.data.action === 'delete') {
                    deleteDevice();
                }
            },
        });
    }, [deleteDevice, presentActionSheet]);

    const handleCreateDeviceFormSubmit = useCallback(
        async (
            data: DevicesCreateFormValues,
            actions: FormikHelpers<DevicesCreateFormValues>
        ) => {
            const noChanges =
                data.macAddress.toUpperCase() ===
                device?.macAddress.toUpperCase();

            if (noChanges) {
                showSuccessMessage();
                return;
            }

            actions.setSubmitting(true);

            try {
                const devicesInCache = rooms.reduce(
                    (acc: THomeAssetData[], r) => {
                        return [...acc, ...r.devices.map((d) => d)];
                    },
                    []
                );
                const newDeviceToSave = {
                    macAddress: data.macAddress,
                    roomId: roomId,
                };
                const createdDevice = await createDeviceRequest(
                    accessToken,
                    jobId,
                    newDeviceToSave
                );
                // Update state management
                saveDevices([...devicesInCache, createdDevice]);
                // Deepomatic: assign a spot to the new device
                await checkAssignDeepomaticTaskgroupIdx(
                    createdDevice.macAddress,
                    deepomatic,
                    setDeepomatic,
                    updateDBState
                );
                updateUrl(createdDevice.id);
                showSuccessMessage();
            } catch (e: any) {
                const response = e.response;
                const errorMessage = response.devices
                    ? response.devices.reduce(
                          (acc: string, message: string) => {
                              return `${acc}${message}<br/>`;
                          },
                          ''
                      )
                    : e.reason;
                presentToast({
                    message: errorMessage,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }

            actions.setSubmitting(false);
        },
        [
            device?.macAddress,
            showSuccessMessage,
            rooms,
            roomId,
            accessToken,
            jobId,
            saveDevices,
            updateUrl,
            presentToast,
            deepomatic,
            setDeepomatic,
            updateDBState,
        ]
    );

    const handleUpdateDeviceFormSubmit = useCallback(
        async (
            data: DevicesUpdateFormValues,
            actions: FormikHelpers<DevicesUpdateFormValues>
        ) => {
            actions.setSubmitting(true);

            try {
                const devicesInCache = rooms.reduce(
                    (acc: THomeAssetData[], r) => {
                        return [...acc, ...r.devices.map((d) => d)];
                    },
                    []
                );
                const [holderPoNumber, holderProductionDate] =
                    data.holderQRCode.split('/');
                const updatedDevice = await updateDeviceRequest(
                    accessToken,
                    jobId,
                    deviceId!,
                    {
                        brand: data.brand,
                        model: data.model,
                        nominalPowerKw: data.nominalPowerKw,
                        location: data.location,
                        heaterLocation: data.heaterLocation,
                        // heaterType: data.heaterType,
                        underWindow: data.underWindow,
                        wallSurface: data.wallSurface,
                        holderPoNumber: holderPoNumber,
                        holderProductionDate: holderProductionDate,
                        fallenDboxNanotapeOnWall:
                            data.fallenDboxNanotapeOnWall,
                        fallenDboxNanotapeType: data.fallenDboxNanotapeType,
                        fallenDboxNanotapeScrewHoles:
                            data.fallenDboxNanotapeScrewHoles,
                    }
                );
                // Update state management
                const updatedDevices = devicesInCache.map((d) =>
                    d.id === deviceId ? updatedDevice : d
                );
                saveDevices(updatedDevices);
                showSuccessMessage();
            } catch (e: any) {
                const response = e.response;
                const errorMessage = response.devices
                    ? response.devices.reduce(
                          (acc: string, message: string) => {
                              return `${acc}${message}<br/>`;
                          },
                          ''
                      )
                    : e.reason;
                presentToast({
                    message: errorMessage,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }

            actions.setSubmitting(false);
        },
        [
            accessToken,
            deviceId,
            jobId,
            presentToast,
            rooms,
            saveDevices,
            showSuccessMessage,
        ]
    );

    return (
        <InterventionPage title="Wire" withPadding>
            <LoadableContent value={value}>
                <Formik
                    initialValues={{ macAddress: device?.macAddress || '' }}
                    validationSchema={DevicesCreateSchema()}
                    validateOnMount={true}
                    onSubmit={handleCreateDeviceFormSubmit}
                >
                    {({
                        values,
                        handleChange,
                        errors,
                        touched,
                        isSubmitting,
                        isValid,
                    }) => (
                        <Form>
                            <div className="ion-margin-bottom">
                                <IonCard>
                                    <IonImg
                                        src="/assets/dbox_location.jpg"
                                        alt="Dbox location"
                                    />
                                </IonCard>
                            </div>
                            <QRCodeScannerInput
                                inputClassName={classNames({
                                    'ion-invalid': errors.macAddress,
                                    'ion-touched': touched.macAddress,
                                })}
                                name="macAddress"
                                value={values.macAddress}
                                label={t`Mac address`}
                                disabled={!!device}
                                onIonInput={handleChange}
                                errorText={errors.macAddress}
                            ></QRCodeScannerInput>
                            {deviceId && (
                                <LoadingButton
                                    className="ion-margin-top"
                                    disabled={isSubmitting}
                                    loading={isDeleting}
                                    onClick={handleDeleteDevice}
                                >
                                    <Trans>Delete Device</Trans>
                                </LoadingButton>
                            )}
                            {!deviceId && (
                                <LoadingButton
                                    className="ion-margin-top"
                                    disabled={!isValid || isDeleting}
                                    loading={isSubmitting}
                                    type="submit"
                                >
                                    <Trans>Save Device</Trans>
                                </LoadingButton>
                            )}
                        </Form>
                    )}
                </Formik>
                {device && (
                    <div>
                        <div className={classNames('ion-margin-top')}>
                            <PhotoUpload
                                maxNumber={MAX_IMAGE_NR}
                                imageCode={`${ImageCategories.heaterDBOXWiring}_${device.macAddress}`}
                                label={t`DBOX Wiring (${device.macAddress})`}
                                mandatory={device.isNew}
                                deepomaticWorkOrderId={
                                    jobDetails?.imageClassificationWorkOrderId
                                }
                                deepomaticTaskGroup={getDeepomaticTaskgroup(
                                    device.macAddress,
                                    DeepomaticTaskGroups.WIRING,
                                    deepomatic
                                )}
                                deviceMacAddress={device.macAddress}
                                controlType={
                                    device.filPilote
                                        ? ControlType.PILOTE
                                        : ControlType.RELAIS
                                }
                                connectionType={
                                    device.hasLte
                                        ? ConnectionType.LTE
                                        : ConnectionType.WIFI
                                }
                            />
                        </div>
                        <div className={classNames('ion-margin-top')}>
                            <PhotoUpload
                                maxNumber={MAX_IMAGE_NR}
                                imageCode={`${ImageCategories.heaterPhotoHeaterLabel}_${device.macAddress}`}
                                label={t`Heater label`}
                                mandatory={device.isNew}
                                deepomaticWorkOrderId={
                                    jobDetails?.imageClassificationWorkOrderId
                                }
                                deepomaticTaskGroup={getDeepomaticTaskgroup(
                                    device.macAddress,
                                    DeepomaticTaskGroups.HEATER_LABEL,
                                    deepomatic
                                )}
                                deviceMacAddress={device.macAddress}
                                controlType={
                                    device.filPilote
                                        ? ControlType.PILOTE
                                        : ControlType.RELAIS
                                }
                                formRef={formRef}
                                deepomaticTasksToFormNames={[
                                    {
                                        taskName:
                                            DeepomaticTasksWithOCR.LABEL_BRAND,
                                        fieldName: `brand`,
                                        fieldDisplayName: 'Brand',
                                    },
                                    {
                                        taskName:
                                            DeepomaticTasksWithOCR.LABEL_MODEL,
                                        fieldName: `model`,
                                        fieldDisplayName: 'Model',
                                    },
                                ]}
                            />
                        </div>

                        <Formik
                            initialValues={{
                                brand: device?.brand || '',
                                model: device?.model || '',
                                nominalPowerKw: device?.nominalPowerKw || '',
                                location: device?.location || '',
                                heaterLocation: device?.heaterLocation || '',
                                // heaterType: device?.heaterType || '',
                                underWindow: device?.underWindow || '',
                                wallSurface: device?.wallSurface || '',
                                holderQRCode:
                                    device.holderPoNumber &&
                                    device.holderProductionDate
                                        ? `${device?.holderPoNumber}/${device?.holderProductionDate}`
                                        : '',
                                fallenDboxNanotapeOnWall:
                                    device?.fallenDboxNanotapeOnWall || '',
                                fallenDboxNanotapeType:
                                    device?.fallenDboxNanotapeType || '',
                                fallenDboxNanotapeScrewHoles:
                                    device?.fallenDboxNanotapeScrewHoles || '',
                            }}
                            validationSchema={DevicesUpdateSchema(
                                // device.holderScanSkipped === 'yes'
                                true,
                                room?.interventionReasonCodes
                            )}
                            validateOnMount={true}
                            onSubmit={handleUpdateDeviceFormSubmit}
                            innerRef={formRef}
                        >
                            {({
                                values,
                                handleChange,
                                handleBlur,
                                errors,
                                touched,
                                isSubmitting,
                                isValid,
                                setFieldValue,
                            }) => (
                                <Form>
                                    <QRCodeScannerInput
                                        inputClassName={classNames({
                                            'ion-invalid': errors.holderQRCode,
                                            'ion-touched':
                                                touched.holderQRCode,
                                        })}
                                        name="holderQRCode"
                                        value={values.holderQRCode}
                                        label={t`Scan holder QR code (if not possible, call supervisor)`}
                                        onIonInput={handleChange}
                                        errorText={errors.holderQRCode}
                                    ></QRCodeScannerInput>
                                    <DefaultInput
                                        className={classNames({
                                            'ion-invalid': errors.brand,
                                            'ion-touched': touched.brand,
                                        })}
                                        name={'brand'}
                                        value={values.brand}
                                        placeholder={t`Enter at least 3 characters`}
                                        label={t`Heater brand`}
                                        onIonInput={(e) => {
                                            handleBrandOnInput(
                                                e,
                                                handleChange
                                            );
                                        }}
                                        onIonBlur={(e) => {
                                            handleBrandOnBlur(e, handleBlur);
                                        }}
                                        errorText={errors.brand}
                                    >
                                        <IonIcon
                                            className={
                                                values.brand !== device?.brand
                                                    ? classes.iconShown
                                                    : classes.iconNotShown
                                            }
                                            icon={closeCircleOutline}
                                            slot={'end'}
                                            onClick={() => {
                                                handleFieldReset(
                                                    'brand',
                                                    setFieldValue
                                                );
                                            }}
                                        />
                                    </DefaultInput>

                                    {showHeaterBrandList && (
                                        <IonList>
                                            {heaterBrands.map((brand, i) => (
                                                <IonItem
                                                    key={i + brand}
                                                    onClick={() =>
                                                        handleHeaterBrandClick(
                                                            brand,
                                                            setFieldValue
                                                        )
                                                    }
                                                >
                                                    {brand}
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    )}
                                    <DefaultInput
                                        className={classNames({
                                            'ion-invalid': errors.model,
                                            'ion-touched': touched.model,
                                        })}
                                        name={'model'}
                                        value={values.model}
                                        placeholder={t`Enter at least 3 characters`}
                                        label={t`Heater model`}
                                        onIonInput={(e) => {
                                            handleModelOnInput(
                                                e,
                                                handleChange,
                                                values.brand
                                            );
                                        }}
                                        onIonBlur={(e) => {
                                            handleModelOnBlur(e, handleBlur);
                                        }}
                                        errorText={errors.model}
                                    >
                                        <IonIcon
                                            className={
                                                values.model !== device?.model
                                                    ? classes.iconShown
                                                    : classes.iconNotShown
                                            }
                                            icon={closeCircleOutline}
                                            slot={'end'}
                                            onClick={() =>
                                                handleFieldReset(
                                                    'model',
                                                    setFieldValue
                                                )
                                            }
                                        />
                                    </DefaultInput>
                                    {showHeaterModelList && (
                                        <IonList>
                                            {heaterModels.map((m) => (
                                                <IonItem
                                                    key={m.assetReferenceId}
                                                    onClick={() =>
                                                        handleHeaterModelClick(
                                                            m.model,
                                                            m.brand,
                                                            setFieldValue
                                                        )
                                                    }
                                                >
                                                    {m.model}
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    )}
                                    <DefaultInput
                                        className={classNames({
                                            'ion-invalid':
                                                errors.nominalPowerKw,
                                            'ion-touched':
                                                touched.nominalPowerKw,
                                        })}
                                        name={'nominalPowerKw'}
                                        value={values.nominalPowerKw}
                                        label={t`Heater nominal power`}
                                        placeholder={t`Nominal power in kWh. Example: 1.50`}
                                        onIonInput={handleChange}
                                        onIonBlur={handleBlur}
                                        errorText={errors.nominalPowerKw}
                                    ></DefaultInput>
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid':
                                                errors.heaterLocation,
                                            'ion-touched':
                                                touched.heaterLocation,
                                        })}
                                        name="heaterLocation"
                                        value={values.heaterLocation}
                                        label={t`What type of wall is the heater attached to?`}
                                        placeholder={t`Select an option`}
                                        onIonChange={handleChange}
                                        onIonBlur={handleBlur}
                                        helperText={t`Does the wall on which the radiator is fixed face the interior or exterior of the house?`}
                                        errorText={errors.heaterLocation}
                                    >
                                        {deviceHeaterLocationTypes.map(
                                            (
                                                deviceHeaterLocationType,
                                                index
                                            ) => (
                                                <IonSelectOption
                                                    key={
                                                        deviceHeaterLocationType.value
                                                    }
                                                    value={
                                                        deviceHeaterLocationType.value
                                                    }
                                                >
                                                    <TransServerMessage
                                                        id={
                                                            deviceHeaterLocationType.label
                                                        }
                                                    />
                                                </IonSelectOption>
                                            )
                                        )}
                                    </DefaultSelect>
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid': errors.wallSurface,
                                            'ion-touched': touched.wallSurface,
                                        })}
                                        name="wallSurface"
                                        value={values.wallSurface}
                                        label={t`What is the surface of the wall?`}
                                        placeholder={t`Select an option`}
                                        onIonChange={handleChange}
                                        onIonBlur={handleBlur}
                                        errorText={errors.wallSurface}
                                    >
                                        {deviceWallSurfaceTypes.map(
                                            (deviceWallSurfaceType) => (
                                                <IonSelectOption
                                                    key={
                                                        deviceWallSurfaceType.value
                                                    }
                                                    value={
                                                        deviceWallSurfaceType.value
                                                    }
                                                >
                                                    <TransServerMessage
                                                        id={
                                                            deviceWallSurfaceType.label
                                                        }
                                                    />
                                                </IonSelectOption>
                                            )
                                        )}
                                    </DefaultSelect>
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid': errors.underWindow,
                                            'ion-touched': touched.underWindow,
                                        })}
                                        name="underWindow"
                                        value={values.underWindow}
                                        label={t`Is the heater under a window?`}
                                        placeholder={t`Select an option`}
                                        onIonChange={handleChange}
                                        onIonBlur={handleBlur}
                                        errorText={errors.underWindow}
                                    >
                                        {deviceUnderWindowTypes.map(
                                            (deviceUnderWindowType) => (
                                                <IonSelectOption
                                                    key={
                                                        deviceUnderWindowType.value
                                                    }
                                                    value={
                                                        deviceUnderWindowType.value
                                                    }
                                                >
                                                    <TransServerMessage
                                                        id={
                                                            deviceUnderWindowType.label
                                                        }
                                                    />
                                                </IonSelectOption>
                                            )
                                        )}
                                    </DefaultSelect>
                                    <DefaultInput
                                        className={classNames({
                                            'ion-invalid': errors.location,
                                            'ion-touched': touched.location,
                                        })}
                                        name="location"
                                        value={values.location}
                                        label={t`Heater location`}
                                        onIonInput={handleChange}
                                        helperText={t`Describe where the heater is in the room to differentiate it from the others`}
                                        errorText={errors.location}
                                    ></DefaultInput>
                                    {room!.interventionReasonCodes
                                        .map((el) => el.code)
                                        .includes('RIR_0001') && (
                                        <>
                                            <DefaultSelect
                                                className={classNames({
                                                    'ion-invalid':
                                                        errors.fallenDboxNanotapeOnWall,
                                                    'ion-touched':
                                                        touched.fallenDboxNanotapeOnWall,
                                                })}
                                                name="fallenDboxNanotapeOnWall"
                                                value={
                                                    values.fallenDboxNanotapeOnWall
                                                }
                                                label={t` Is the nanotape of fallen D-Box still on the wall?`}
                                                placeholder={t`Select an option`}
                                                onIonChange={handleChange}
                                                onIonBlur={handleBlur}
                                                errorText={
                                                    errors.fallenDboxNanotapeOnWall
                                                }
                                            >
                                                {deviceFallenDboxNanotapeOnWallTypes.map(
                                                    (
                                                        deviceFallenDboxNanotapeOnWallType
                                                    ) => (
                                                        <IonSelectOption
                                                            key={
                                                                deviceFallenDboxNanotapeOnWallType.value
                                                            }
                                                            value={
                                                                deviceFallenDboxNanotapeOnWallType.value
                                                            }
                                                        >
                                                            <TransServerMessage
                                                                id={
                                                                    deviceFallenDboxNanotapeOnWallType.label
                                                                }
                                                            />
                                                        </IonSelectOption>
                                                    )
                                                )}
                                            </DefaultSelect>
                                            <DefaultSelect
                                                className={classNames({
                                                    'ion-invalid':
                                                        errors.fallenDboxNanotapeType,
                                                    'ion-touched':
                                                        touched.fallenDboxNanotapeType,
                                                })}
                                                name="fallenDboxNanotapeType"
                                                value={
                                                    values.fallenDboxNanotapeType
                                                }
                                                label={t`Is the nanotape hard or soft?`}
                                                placeholder={t`Select an option`}
                                                onIonChange={handleChange}
                                                onIonBlur={handleBlur}
                                                errorText={
                                                    errors.fallenDboxNanotapeType
                                                }
                                            >
                                                {deviceFallenDboxNanotapeTypeTypes.map(
                                                    (
                                                        deviceFallenDboxNanotapeTypeType
                                                    ) => (
                                                        <IonSelectOption
                                                            key={
                                                                deviceFallenDboxNanotapeTypeType.value
                                                            }
                                                            value={
                                                                deviceFallenDboxNanotapeTypeType.value
                                                            }
                                                        >
                                                            <TransServerMessage
                                                                id={
                                                                    deviceFallenDboxNanotapeTypeType.label
                                                                }
                                                            />
                                                        </IonSelectOption>
                                                    )
                                                )}
                                            </DefaultSelect>
                                            <DefaultSelect
                                                className={classNames({
                                                    'ion-invalid':
                                                        errors.fallenDboxNanotapeScrewHoles,
                                                    'ion-touched':
                                                        touched.fallenDboxNanotapeScrewHoles,
                                                })}
                                                name="fallenDboxNanotapeScrewHoles"
                                                value={
                                                    values.fallenDboxNanotapeScrewHoles
                                                }
                                                label={t`Does the nanotape have screw holes?`}
                                                placeholder={t`Select an option`}
                                                onIonChange={handleChange}
                                                onIonBlur={handleBlur}
                                                errorText={
                                                    errors.fallenDboxNanotapeScrewHoles
                                                }
                                            >
                                                {deviceFallenDboxNanotapeScrewHolesTypes.map(
                                                    (
                                                        deviceFallenDboxNanotapeScrewHolesType
                                                    ) => (
                                                        <IonSelectOption
                                                            key={
                                                                deviceFallenDboxNanotapeScrewHolesType.value
                                                            }
                                                            value={
                                                                deviceFallenDboxNanotapeScrewHolesType.value
                                                            }
                                                        >
                                                            <TransServerMessage
                                                                id={
                                                                    deviceFallenDboxNanotapeScrewHolesType.label
                                                                }
                                                            />
                                                        </IonSelectOption>
                                                    )
                                                )}
                                            </DefaultSelect>
                                            <PhotoUpload
                                                className="ion-margin-top"
                                                maxNumber={MAX_IMAGE_NR}
                                                imageCode={`${ImageCategories.dboxHolder}_${device.macAddress}`}
                                                label={t`Photo of the fallen d-box's holder and the wall`}
                                                mandatory={true}
                                            />
                                        </>
                                    )}
                                    <LoadingButton
                                        className="ion-margin-top"
                                        disabled={
                                            !isValid ||
                                            isDeleting ||
                                            !photosUploaded
                                        }
                                        loading={isSubmitting}
                                        type="submit"
                                    >
                                        <Trans>Save Device</Trans>
                                    </LoadingButton>
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}
            </LoadableContent>
        </InterventionPage>
    );
};

export default Devices;
